/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Card as MuiCard,
  Box,
  Typography as Typo,
  styled,
  TypographyProps,
  Skeleton,
  Grid,
  Tooltip,
} from '@mui/material';
import {useSelector, useDispatch} from 'react-redux';
import {AppState} from '@jobseeker/common-core/types';
import {saveUserSettings} from '@jobseeker/common-core/reducers/user/thunkServices';
import {useEffect, useState} from 'react';
import {useIsLargeDevice} from '@jobseeker/lotus/hooks';
import {Image} from '@jobseeker/lotus/components/Image';
import Checkbox from '@jobseeker/lotus/elements/CheckBox';
import {
  BlankTickHirist,
  CheckHirist,
  Information,
} from '@jobseeker/lotus/assets/icons';
import Button from '@jobseeker/lotus/elements/Button';
import {enqueueSnackbar} from 'notistack';
import Loader from '@jobseeker/lotus/components/Loader';

const settings = [
  {
    heading: 'Daily New Jobs',
    description: 'Get access to top matching job in your Inbox.',
    proFeature: false,
    key: 'jobFeed',
  },
  {
    heading: 'Applied Jobs',
    description:
      'Receive Application confirmation emails right after applying to any job.',
    proFeature: false,
    key: 'jobApply',
  },
  {
    heading: 'Follow-up Credited',
    description: 'Get notification for follow-up credited in your account.',
    proFeature: true,
    key: 'followUp',
  },
  {
    heading: 'Follow-up Used',
    description: 'Get notification for follow-up used.',
    proFeature: true,
    key: 'followUpUsed',
  },
  {
    heading: 'Pending Test',
    description: 'Enable, Pending test notification email.',
    proFeature: true,
    key: 'testPending',
  },
  {
    heading: 'Promotional',
    description: 'Receive good job offers from recruiters.',
    proFeature: false,
    key: 'promotions',
  },
  {
    heading: 'Chat Notifications',
    description: 'Receive chat notifications in your inbox.',
    proFeature: false,
    key: 'chat',
  },
  {
    heading: 'Educational-Learn & Grow',
    description: 'Receive courses notifications based on your profile.',
    proFeature: false,
    key: 'education',
  },
  {
    heading: 'Recruiter Actions',
    description: 'Receive Recruiter action email in your inbox.',
    proFeature: false,
    key: 'jobAction',
  },
  {
    heading: 'Recruiters viewing my profile',
    description: 'Get to know who is viewing your profile.',
    proFeature: false,
    key: 'search',
  },
  {
    heading: 'Jobs Relevant for me',
    description: 'Receive interesting relevant jobs for your profile.',
    proFeature: false,
    key: 'boost',
  },
  {
    heading: 'Unpublished Job Notification',
    description: 'Get updates when jobs are unpublished',
    proFeature: false,
    key: 'unpublish',
  },
  {
    heading: 'Chat Email Notification',
    description: 'Get notified with an email about new chat messages from recruiters',
    proFeature: false,
    key: 'chatEmailNotification',
  }
];

const Card = styled(MuiCard)(({theme}) => ({
  borderRadius: theme.spacing(2),
}));

const Typography = styled(Typo)<TypographyProps>(() => ({
  display: 'flex',
  alignItems: 'center',
}));

const NotificationSettingWidget = (props: {loading: boolean}) => {
  const {loading} = props;
  const [buttonLoading, setButtonLoading] = useState<boolean>(false);
  const dispatch: any = useDispatch();

  const isLarge = useIsLargeDevice();

  const userSettings = useSelector(
    (state: AppState) => state.user.profile.settings
  );

  const [updatedSettings, setUpdatedSettings] = useState(userSettings);

  useEffect(() => {
    setUpdatedSettings(userSettings);
  }, [userSettings]);

  useEffect(() => {
    console.log('Updated Settings:', updatedSettings);
  }, [updatedSettings]);

  const handleSettingChange = (key: string) => {
    setUpdatedSettings(prev => {
      const newValue = prev[key] === 1 ? 0 : 1;
      console.log(`Updating ${key} to ${newValue}`);
      return {
        ...prev,
        [key]: newValue,
      };
    });
  };

  const handleSaveSettings = async () => {
    const payload = {...updatedSettings};

    try {
      setButtonLoading(true);
      await dispatch(saveUserSettings(payload)).unwrap();
      enqueueSnackbar('Settings have been saved successfully.', {
        variant: 'success',
      });
    } catch (error) {
      enqueueSnackbar('Failed to save settings.', {
        variant: 'error',
      });
    } finally {
      setButtonLoading(false);
    }
  };

  return (
    <Box height={'100%'} px={{xxs: 1.25, md: 0}}>
      <Card
        sx={{
          boxShadow: 2,
          borderRadius: 2,
          marginLeft: {md: 0.625},
          px: {xxs: 1.875, md: 4.625},
          pb: {xxs: 2, md: 0},
          width: '100%',
          height: {xxs: 'auto', md: '100%'},
        }}
        data-test-id="notification_setting_container"
      >
        <Box>
          {isLarge && (
            <Typography
              display="flex"
              fontSize={{xxs: 16, md: 20}}
              fontWeight={400}
              mb={1}
              mt={2.75}
              sx={{
                wordWrap: 'break-word',
                textTransform: 'capitalize',
              }}
            >
              Email & Push Notifications
            </Typography>
          )}
          <Grid
            container
            mt={-1}
            spacing={2}
            justifyContent="flex-start"
            alignItems="flex-start"
          >
            {settings.map((ele, index) => (
              <Grid key={index} item xs={12} md={6} lg={4}>
                {loading ? (
                  <Box display="flex" justifyContent="space-between" pr={3}>
                    <Box width="100%">
                      <Skeleton
                        variant="rounded"
                        width="50%"
                        height={16}
                        sx={{mt: {xxs: 0, md: 1}, mb: 1}}
                      />
                      <Skeleton
                        variant="rounded"
                        width="30%"
                        height={12}
                        sx={{mt: {xxs: 0, md: 1}, mb: 1}}
                      />
                    </Box>
                    <Skeleton
                      variant="rounded"
                      width={100}
                      height={25}
                      sx={{mt: 1}}
                    />
                  </Box>
                ) : (
                  <Box display="flex" alignItems="center">
                    <Box display="flex" alignItems="center">
                      <Checkbox
                        size={18}
                        id={`setting-${index}`}
                        name={`setting-${index}`}
                        checked={!!updatedSettings[ele.key]}
                        onChange={() => handleSettingChange(ele.key)}
                        data-testid="checkbox"
                        icon={
                          <Image
                            src={BlankTickHirist}
                            alt="unchecked"
                            width={18}
                            height={18}
                          />
                        }
                        label={
                          <Box ml={1} mr={-1.75}>
                            <Typography
                              variant="body1"
                              fontSize={{xxs: 14, md: 16}}
                              sx={{color: '#555', cursor: 'pointer'}}
                            >
                              {ele.heading}
                            </Typography>
                          </Box>
                        }
                        checkedIcon={
                          <Image
                            src={CheckHirist}
                            alt="checked"
                            width={18}
                            height={18}
                          />
                        }
                      />
                      <Tooltip
                        placement="bottom"
                        arrow
                        title={
                          <Typography
                            variant="subtitle2"
                            sx={{
                              textAlign: 'center',
                              whiteSpace: 'normal',
                              overflowWrap: 'break-word',
                              backgroundColor: '#1c2d41',
                              color: '#fff',
                              padding: '8px',
                              borderRadius: '4px',
                            }}
                          >
                            {ele.description}
                          </Typography>
                        }
                        componentsProps={{
                          tooltip: {
                            sx: {
                              backgroundColor: '#1c2d41',
                              color: '#fff',
                            },
                          },
                          arrow: {
                            sx: {
                              color: '#1c2d41',
                            },
                          },
                        }}
                        disableHoverListener={!isLarge}
                        disableTouchListener={isLarge}
                      >
                        <Box sx={{display: 'flex'}}>
                          <Image
                            src={Information}
                            alt="info"
                            width={18}
                            height={18}
                            style={{marginLeft: 4}}
                          />
                        </Box>
                      </Tooltip>
                    </Box>
                  </Box>
                )}
              </Grid>
            ))}
          </Grid>
        </Box>
      </Card>
      <Box display={'flex'} gap={1.5} mt={4} mb={2} ml={{xxs: 0, md: 1}}>
        <Button
          sx={{
            width: {xxs: '100%', md: '191px'},
            fontSize: '16px',
            backgroundColor: '#e9630c',
            px: 5.75,
            py: 1.375,
            borderRadius: '3px',
            height: '50px',
            '&:hover': {
              backgroundColor: '#e79e00',
            },
          }}
          variant="contained"
          onClick={handleSaveSettings}
        >
          {buttonLoading ? (
            <Loader
              size={20}
              sx={{
                color: '#ffffff !important',
              }}
            />
          ) : (
            'Save Settings'
          )}
        </Button>
      </Box>
    </Box>
  );
};

export default NotificationSettingWidget;
